<template>
  <v-container class="edit-device-page" fluid>
    <v-breadcrumbs :items="items" class="px-0 pt-0 pb-2" />
    <div class="edit-device-page__header">
      <h1 class="edit-device-page__title">Редактирование устройства</h1>
      <v-btn text class="text-none" color="primary" to="/device">
        <v-icon left small>mdi-arrow-left</v-icon>
        К списку устройств
      </v-btn>
    </div>

    <v-form v-if="isForm" ref="form" v-model="valid" lazy-validation>
      <v-card class="edit-device-page__card" flat outlined>
        <div class="edit-device-page__section">
          <div class="edit-device-page__section-title">
            <v-icon left small>mdi-format-list-bulleted-type</v-icon>
            Основные данные
          </div>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete
                label="Тип устройства"
                :items="typeDevices"
                v-model="typeDevice"
                item-text="name"
                item-value="value"
                prepend-inner-icon="mdi-format-list-bulleted-type"
                outlined
                dense
                hide-details
                disabled
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="IP-адрес"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-ip-outline"
                :disabled="typeDevice === 'G'"
                :required="typeDevice !== 'G'"
                v-model="ip_address"
                :rules="validateIpAddress()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="Серийный номер"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-counter"
                v-model="serial_number"
                :rules="typeDevice === 'G' ? [] : [() => !!serial_number || 'Обязательное поле']"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="MAC-адрес"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-access-point"
                v-model="mac_address"
                :rules="typeDevice === 'G' ? [] : validateMacAddress()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="Производитель устройства"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-cogs"
                readonly
                v-model="manufacturer"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="Модель"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-cog-outline"
                readonly
                v-model="model"
              />
            </v-col>
            <v-col v-if="roleName !== 'agent'" cols="12" sm="6" md="4">
              <v-autocomplete
                label="Агент"
                :items="organizations"
                item-text="user.name"
                item-value="id"
                prepend-inner-icon="mdi-account-search"
                v-model="agent"
                :rules="[() => !!agent || 'Обязательное поле']"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-textarea
                label="Технический комментарий"
                outlined
                dense
                hide-details
                auto-grow
                rows="2"
                prepend-inner-icon="mdi-tools"
                placeholder="IP, версия прошивки, заметки по настройке"
                v-model="technical_comment"
                :disabled="!isTechnicalCommentEditable"
              />
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-0" />

        <div class="edit-device-page__section">
          <div class="edit-device-page__section-title">
            <v-icon left small>mdi-phone-in-talk-outline</v-icon>
            SIP и связь
          </div>
          <v-row align="center">
            <v-col cols="12" sm="6" md="4" v-if="typeDevice !== 'G'">
              <v-checkbox
                label="Настройки SIP"
                hide-details
                :disabled="typeDevice === 'D' || typeDevice === 'C'"
                v-model="isActiveSipSettings"
                dense
                color="primary"
                class="mt-0"
              />
            </v-col>
          </v-row>
          <v-row v-if="isActiveSipSettings">
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="SIP имя"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-rename-box-outline"
                v-model="sip.sipName"
                :rules="[() => !isActiveSipSettings || !!sip.sipName || 'Обязательное поле']"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="SIP пароль"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-lock-alert-outline"
                v-model="sip.sipPassword"
                :rules="[() => !isActiveSipSettings || !!sip.sipPassword || 'Обязательное поле']"
              />
            </v-col>
            <v-col v-if="typeDevice === 'B'" cols="12" sm="6" md="4">
              <v-text-field
                label="SIP сервер"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-clipboard-alert-outline"
                v-model="sip.sipAddress"
              />
            </v-col>
          </v-row>
          <v-row v-if="typeDevice === 'G'">
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="GSM телефон"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-phone"
                v-model="gsm_phone"
                @input="handleGsmPhoneInput"
                :rules="validateGsmPhone"
                :maxlength="16"
                placeholder="+7 (XXX) XXX-XX-XX"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="Городской номер (переадресация)"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-phone-in-talk"
                v-model="phone_number"
                disabled
                :rules="[]"
              />
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-0" />

        <div class="edit-device-page__actions">
          <v-btn
            color="primary"
            class="text-none"
            rounded
            @click.prevent="updateEdit"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            Сохранить
          </v-btn>
          <v-btn
            color="primary"
            outlined
            class="text-none"
            rounded
            @click.prevent="returnDevice()"
          >
            <v-icon left>mdi-close</v-icon>
            Отменить
          </v-btn>
        </div>
      </v-card>
    </v-form>
  </v-container>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import formatPhoneNumberTextField from '@/mixins/formatPhoneNumberTextField';

export default {
  name: 'DeviceEdit',
  props: ['id'],
  mixins: [formatPhoneNumberTextField],
  data () {
    return {
      sip: {
        sipName: '',
        sipPassword: '',
        sipAddress: '',
      },
      valid: true,
      isForm: false,
      ip_address: '',
      serial_number: null,
      isActiveSipSettings: true,
      mac_address: '',
      manufacturer: '',
      model: '',
      agent: '',
      model_id: '',
      typeDevice: '',
      items: [
        {
          text: 'Устройства',
          disabled: false,
          href: '/device',
        },
        {
          text: 'Редактирование устройства',
          disabled: true,
          href: 'breadcrumbs_link_1',
        },
      ],
      typeDevices: [
        {
          value: 'D',
          name: 'Домофон',
        },
        {
          value: 'B',
          name: 'ГРЗ',
        },
        {
          value: 'C',
          name: 'Контроллер',
        },
        {
          value: 'G',
          name: 'GSM модуль',
        },
      ],
      gsm_phone: '',
      city_phone_number_id: null,
      phone_number: '',
      technical_comment: '',
    };
  },
  created () {
    // this.loadAllAgents();
    this.getDevice(this.id)
      .then(() => {
        this.isForm = true;
        this.ip_address = this.device.ip_address;
        this.serial_number = this.device.serial_number;
        this.manufacturer = this.device.manufacturer;
        this.model_id = this.device.model_id;
        this.typeDevice = this.device.type;
        this.model = this.device.model;
        this.agent = this.device.agent_id;
        this.technical_comment = this.device.technical_comment || '';
        const sipSettings = this.device.sip_settings;
        const isMacAddress = this.device.mac_address;
        if (isMacAddress) {
          this.mac_address = this.device.mac_address;
        }
        if (this.typeDevice === 'G') {
          const phoneDigits = (this.device.gsm_phone || '').replace(/\D/g, '');
          const phoneToFormat = phoneDigits.length === 10 ? '7' + phoneDigits : (this.device.gsm_phone || '');
          this.gsm_phone = phoneToFormat ? this.formatPhoneNumberTextField(phoneToFormat) : '';
          this.city_phone_number_id = this.device.city_phone_number_id || null;
          this.phone_number = this.device.city_phone || '';
          this.isActiveSipSettings = false;
        } else {
          if (!sipSettings) {
            this.isActiveSipSettings = false;
          }
          if (sipSettings) {
            if (sipSettings.sip_name) {
              this.sip.sipName = sipSettings.sip_name;
            }
            if (sipSettings.sip_password) {
              this.sip.sipPassword = sipSettings.sip_password;
            }
            if (sipSettings.sip_address) {
              this.sip.sipAddress = sipSettings.sip_address;
            }
          }
        }
      })
      .catch((err) => {
        if (err.message.includes('422')) {
          const response = err.response.data;
          for (const input in response) {
            response[input].forEach(error => this.$flash_warning(input + ': ' + error));
          }
        } else {
          this.$flash_warning('Произошла ошибка, обновите страницу!');
        }
      });
  },
  computed: {
    ...mapGetters({
      device: 'Device/device',
      organizations: 'Agents/agents',
      roleName: 'roleName',
    }),
    ...mapState({
      deviceState: 'Device/device',
    }),
    validateGsmPhone () {
      const rules = [];
      rules.push(() => !!this.gsm_phone || 'Обязательное поле');
      if (this.gsm_phone) {
        const digitCount = this.gsm_phone.replace(/\D/g, '').length;
        rules.push(() => (digitCount >= 11 && digitCount <= 11) || 'Номер должен содержать 11 цифр (с 7).');
      }
      return rules;
    },
    isTechnicalCommentEditable () {
      return ['admin', 'admin-engineer'].includes(this.roleName);
    },
  },
  methods: {
    ...mapActions({
      getDevice: 'Device/getDevice',
      getDevices: 'Device/getDevices',
      updateDevice: 'Device/updateDevice',
      updateDeviceBarrier: 'Device/updateDeviceBarrier',
      updateDeviceController: 'Device/updateDeviceController',
      updateDeviceGsm: 'Device/updateDeviceGsm',
      getDeviceFreeCityPhone: 'Device/getDeviceFreeCityPhone',
      loadAllAgents: 'Agents/loadAllAgents',
    }),
    validateIpAddress () {
      const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      const isRequired = this.typeDevice !== 'G';
      return [
        v => {
          const value = (v != null ? String(v) : '').trim();
          if (!value) return isRequired ? 'Обязательное поле' : true;
          return ipRegex.test(value) || 'Укажите корректный ip адрес';
        },
      ];
    },
    validateMacAddress () {
      const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})|([0-9a-fA-F]{4}\.[0-9a-fA-F]{4}\.[0-9a-fA-F]{4})$/;
      const isRequired = this.typeDevice !== 'C' && this.typeDevice !== 'G';
      return [
        v => {
          const value = (v != null ? String(v) : '').trim();
          if (!value) return isRequired ? 'Обязательное поле' : true;
          return macRegex.test(value) || 'Укажите корректный mac адрес';
        },
      ];
    },
    updateEdit () {
      const isFormValid = this.$refs.form.validate();
      const canSubmitGsm = this.typeDevice === 'G' ? this.isValidGsmSubmit() : false;
      const canSubmit = isFormValid || canSubmitGsm;

      if (canSubmit) {
        const dataDevice = {
          ip_address: this.ip_address,
          serial_number: this.serial_number,
        };

        if (this.typeDevice === 'C' || this.typeDevice === 'G') {
          if (this.mac_address) {
            dataDevice.mac_address = this.mac_address;
          }
        } else {
          dataDevice.mac_address = this.mac_address;
        }

        if (this.typeDevice === 'G') {
          dataDevice.gsm_phone = this.normalizeGsmPhone(this.gsm_phone);
          if (this.city_phone_number_id) {
            dataDevice.city_phone_number_id = this.city_phone_number_id;
          }
          if (this.ip_address) {
            dataDevice.ip_address = this.ip_address;
          }
        } else {
          dataDevice.ip_address = this.ip_address;
          if (this.isActiveSipSettings && this.sip.sipName.length > 0) {
            dataDevice.sip_name = this.sip.sipName;
          }

          if (this.isActiveSipSettings && this.sip.sipPassword.length > 0) {
            dataDevice.sip_password = this.sip.sipPassword;
          }

          if (this.isActiveSipSettings && this.typeDevice === 'B') {
            dataDevice.sip_address = this.sip.sipAddress;
          }
        }

        if (this.roleName !== 'agent') {
          dataDevice.agent_id = this.agent;
        }
        if (this.isTechnicalCommentEditable) {
          dataDevice.technical_comment = this.technical_comment ? this.technical_comment : null;
        }

        if (this.typeDevice === 'B') {
          this.updateDeviceBarrier({
            id: this.device.id,
            dataDevice,
          })
            .then(() => {
              this.navigateToDevice();
              this.$flash_success('Изменения сохранены');
            })
            .catch((err) => {
              this.handleDeviceCreationError(err);
            });
        } else if (this.typeDevice === 'C') {
          this.updateDeviceController({
            id: this.device.id,
            dataDevice,
          })
            .then(() => {
              this.navigateToDevice();
              this.$flash_success('Изменения сохранены');
            })
            .catch((err) => {
              this.handleDeviceCreationError(err);
            });
        } else if (this.typeDevice === 'G') {
          this.updateDeviceGsm({
            id: this.device.id,
            dataDevice,
          })
            .then(() => {
              this.navigateToDevice();
              this.$flash_success('Изменения сохранены');
            })
            .catch((err) => {
              this.handleDeviceCreationError(err);
            });
        } else {
          dataDevice.type = this.typeDevice;
          this.updateDevice({
            id: this.device.id,
            dataDevice,
          })
            .then(() => {
              this.navigateToDevice();
              this.$flash_success('Изменения сохранены');
            })
            .catch((err) => {
              this.handleDeviceCreationError(err);
            });
        }
      } else {
        this.$flash_warning('Заполните обязательные поля формы');
      }
    },
    isValidGsmSubmit () {
      const digitCount = (this.gsm_phone || '').replace(/\D/g, '').length;
      const hasValidGsmPhone = digitCount === 11;
      const hasAgent = this.roleName === 'agent' || !!this.agent;
      return hasValidGsmPhone && hasAgent;
    },
    handleDeviceCreationError (err) {
      if (err.message.includes('422')) {
        const response = err.response.data;
        for (const input in response) {
          response[input].forEach(error => this.$flash_warning(input + ': ' + error));
        }
      } else {
        this.$flash_warning('Не удалось сохранить изменения');
      }
    },
    normalizeGsmPhone (phone) {
      if (!phone) return '';
      let digitsOnly = phone.replace(/\D/g, '');
      if (digitsOnly.startsWith('7')) {
        digitsOnly = digitsOnly.slice(1);
      } else if (digitsOnly.startsWith('8')) {
        digitsOnly = digitsOnly.slice(1);
      }
      return digitsOnly.slice(0, 10);
    },
    handleGsmPhoneInput (value) {
      this.gsm_phone = this.formatPhoneNumberTextField(value);
    },
    navigateToDevice () {
      this.$router.push({ name: 'Device' }).catch((err) => {
        if (!err || err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
    returnDevice () {
      this.navigateToDevice();
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-device-page {
  max-width: 90%;
}

.edit-device-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}

.edit-device-page__title {
  font-size: 1.5rem;
  font-weight: 400;
  color: #37474f;
  margin: 0;
  letter-spacing: -0.02em;
}

.edit-device-page__card {
  border-radius: 12px;
  overflow: hidden;
}

.edit-device-page__section {
  padding: 20px 24px;
}

.edit-device-page__section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #546e7a;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
}

.edit-device-page__actions {
  padding: 16px 24px 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

@media screen and (max-width: 600px) {
  .edit-device-page__header {
    flex-direction: column;
    align-items: flex-start;
  }
  .edit-device-page__title {
    font-size: 1.4rem;
  }
}
</style>
